import type Luminary from '@lumen5/luminary';
import { getGlobalFontManager } from '@lumen5/luminary';

import { NO_MUSIC_ID, UNREGISTERED } from '../constants';
import type { MusicDescription, TemplateDescription, VideoDescription } from '../types';

import { formatDurationMs } from './assetFormatters';
import { renderColorAsString } from './assetHelpers';

import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { getColorGradingFilterFromUrl } from 'js/src/data/color-grading/color-grading-selectors';
import type { RootState } from 'js/src/libs/RootState';

export function getTemplateFontFamilies(luminary: Luminary): string[] {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Filter and return only the fonts that are actually used in the template
    const fontFamilies = templateFonts
        .map((url) => {
            let font = fontManager.getFontBySource(url);
            if (!font) {
                // Luminary only loads fonts when building the asset
                font = fontManager.addFont(url);
            }
            return font ? font.familyName : null;
        })
        .filter((font) => font !== null);

    return fontFamilies;
}

export function getTemplateFontFamily(luminary: Luminary, fontURL: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested URL
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && url === fontURL) {
            return font.familyName;
        }
    }

    return null; // No matching font found
}

export function getTemplateFontURL(luminary: Luminary, fontFamily: string): string | null {
    // Get the global font manager to access all available fonts
    const fontManager = getGlobalFontManager();
    const templateFonts = luminary.template?.fonts || [];

    // Find the first font that matches the requested family
    for (const url of templateFonts) {
        const font = fontManager.getFontBySource(url);
        if (font && font.familyName === fontFamily) {
            return url;
        }
    }

    return null; // No matching font found
}

/**
 * The applied color grading filter, named the way set_global_color_grading expects it. Luminary
 * stores the filter as a texture URL, which the agent cannot act on, so it is resolved back to a
 * name through the store. An unresolvable source still reports the raw value rather than claiming
 * nothing is applied.
 */
const getColorGradingDescription = (luminary: Luminary, state: RootState): string => {
    const grading = luminary.template?.grading;
    if (!grading?.source) {
        return 'none';
    }
    const filter = getColorGradingFilterFromUrl(state, grading.source);
    const name = filter?.name ?? grading.source;
    const intensity = grading.intensity ?? 1;
    return intensity < 1 ? `${name} (${Math.round(intensity * 100)}% intensity)` : name;
};

export const getVideoDescription = (luminary: Luminary, state?: RootState): VideoDescription => {
    return {
        resolution: `${luminary.referenceResolution.width}x${luminary.referenceResolution.height}`,
        aspectRatio: luminary.aspectRatio,
        sceneCount: luminary.scenes.length,
        duration: formatDurationMs(luminary.duration),
        // Storeless callers (e.g. the description path) cannot resolve a texture URL to a filter
        // name, so the field is left off rather than reported as an unusable URL.
        ...(state ? { colorGrading: getColorGradingDescription(luminary, state) } : {}),
    };
};

/**
 * Get template metadata including ID, fonts, and color palette.
 * @param luminary The Luminary instance containing template data
 * @param includeColorNames Whether palette colors carry a `(name)` alongside the hex; off for the
 *   Toolbox, whose model reads hex directly.
 * @returns Object with template ID, font families, and color descriptions
 */
export const getTemplateDescription = (
    luminary: Luminary,
    includeColorNames: boolean = true,
): TemplateDescription => {
    return {
        template: luminary.template.id,
        templateFonts: getTemplateFontFamilies(luminary).join(', '),
        templateColors: luminary.template.colors
            .map((item) => renderColorAsString(item.color, includeColorNames))
            .join(', '),
    };
};

export const getVideoMusicDescription = (luminary: Luminary): MusicDescription => {
    // A track is set whenever luminary.music carries a uuid. Report the registry id rather than
    // the asset uuid: `set_music` takes a music_id, so the uuid would be an identifier the agent
    // cannot act on. A set-but-unregistered track (e.g. a template default the agent hasn't
    // searched up) reports UNREGISTERED, keeping it distinct from a video with no music at all
    // rather than both collapsing to the same "No music set" placeholder.
    if (luminary.music?.uuid) {
        const musicRegistry = MusicRegistry.getInstance();
        const musicId = musicRegistry.getItemIdByUuid(luminary.music.uuid);
        const musicRepresentation = musicId ? musicRegistry.getRepresentation(musicId) : undefined;
        return {
            id: musicId ?? UNREGISTERED,
            volume: luminary.musicAsset?.volume ?? 0,
            title: musicRepresentation
                ? musicRepresentation.title || 'Untitled'
                : 'Music set (not in registry)',
        };
    }
    return {
        id: NO_MUSIC_ID,
        volume: 0,
        title: 'No music set',
    };
};
